/**
 * ForecastDialPage — Dedicated Monte Carlo Yield Forecast Page
 * Users can search for pools or enter pool details manually
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import ForecastDial from '../components/ForecastDial';
import { getBackendBaseUrl } from '../lib/apiBase';
import { 
  Search, Gauge, TrendingUp, AlertTriangle, 
  ChevronDown, Sparkles, Info, Crown, RefreshCw
} from 'lucide-react';

const API_URL = getBackendBaseUrl();

// Chain options
const CHAINS = ['ethereum', 'arbitrum', 'polygon', 'optimism', 'base', 'bsc', 'avalanche', 'solana'];

// IL Risk options
const IL_RISKS = [
  { value: 'low', label: 'Low', color: '#10b981' },
  { value: 'medium', label: 'Medium', color: '#f59e0b' },
  { value: 'high', label: 'High', color: '#ef4444' },
];

export default function ForecastDialPage() {
  const { user, isAuthenticated, token } = useAuth();
  const [searchParams] = useSearchParams();
  
  // User tier check
  const userTier = user?.subscription?.tier || 'free';
  const isAdmin = user?.role === 'admin';
  const isElite = userTier === 'elite' || userTier === 'unlimited' || isAdmin;
  
  // Pool search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  
  // Selected pool or manual entry
  const [selectedPool, setSelectedPool] = useState(null);
  const [manualMode, setManualMode] = useState(false);
  const [manualSubmitted, setManualSubmitted] = useState(false); // explicit "Run Forecast" click
  
  // Manual entry fields
  const [manualData, setManualData] = useState({
    pool_id: 'custom-pool',
    symbol: '',
    current_apy: '',
    tvl: '',
    il_risk: 'medium',
    chain: 'ethereum',
    protocol: 'Custom',
  });
  
  // Load pool from URL params
  useEffect(() => {
    const poolId = searchParams.get('pool');
    const symbol = searchParams.get('symbol');
    if (poolId && symbol) {
      // Fetch pool details
      fetchPoolDetails(poolId, symbol);
    }
  }, [searchParams]);
  
  const fetchPoolDetails = async (poolId, symbol) => {
    try {
      const res = await fetch(`${API_URL}/api/yield-intelligence/pools?search=${encodeURIComponent(symbol)}&limit=10`);
      const data = await res.json();
      const pool = data.pools?.find(p => p.pool_id === poolId) || data.pools?.[0];
      if (pool) {
        setSelectedPool(pool);
        setSearchQuery(pool.symbol);
      }
    } catch (err) {
      console.error('Failed to fetch pool:', err);
      toast.error('Failed to load pool details');
    }
  };
  
  // Search pools
  const searchPools = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const res = await fetch(`${API_URL}/api/yield-intelligence/pools?search=${encodeURIComponent(query)}&limit=10`);
      const data = await res.json();
      setSearchResults(data.pools || []);
      setShowResults(true);
    } catch (err) {
      console.error('Search error:', err);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };
  
  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery && !selectedPool) {
        searchPools(searchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, selectedPool]);
  
  // Select a pool from search results
  const handleSelectPool = (pool) => {
    setSelectedPool(pool);
    setSearchQuery(pool.symbol);
    setShowResults(false);
    setManualMode(false);
  };
  
  // Clear selection
  const handleClear = () => {
    setSelectedPool(null);
    setSearchQuery('');
    setSearchResults([]);
    setManualMode(false);
    setManualSubmitted(false);
  };
  
  // Check if manual fields are valid (for showing the Run Forecast button)
  const manualFieldsReady = manualMode && manualData.symbol && manualData.current_apy && manualData.tvl;

  // Get pool data for ForecastDial
  const poolData = useMemo(() => {
    if (selectedPool) {
      return {
        poolId: selectedPool.pool_id,
        symbol: selectedPool.symbol,
        currentApy: selectedPool.apy_total,
        tvl: selectedPool.tvl_usd,
        ilRisk: selectedPool.il_risk || 'medium',
        chain: selectedPool.chain,
        protocol: selectedPool.protocol,
      };
    }
    if (manualMode && manualSubmitted && manualData.symbol && manualData.current_apy && manualData.tvl) {
      return {
        poolId: manualData.pool_id,
        symbol: manualData.symbol,
        currentApy: parseFloat(manualData.current_apy),
        tvl: parseFloat(manualData.tvl),
        ilRisk: manualData.il_risk,
        chain: manualData.chain,
        protocol: manualData.protocol,
      };
    }
    return null;
  }, [selectedPool, manualMode, manualSubmitted, manualData]);
  
  // Format USD
  const formatUSD = (n) => {
    if (!n) return '$0';
    if (n >= 1e9) return `$${(n/1e9).toFixed(2)}B`;
    if (n >= 1e6) return `$${(n/1e6).toFixed(2)}M`;
    if (n >= 1e3) return `$${(n/1e3).toFixed(1)}K`;
    return `$${n.toFixed(0)}`;
  };

  return (
    <div className="min-h-screen bg-[#0a0e17] text-white p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-[#8b5cf6]/20 rounded-xl">
              <Gauge className="w-8 h-8 text-[#8b5cf6]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Forecast Dial</h1>
              <p className="text-gray-400 text-sm">Monte Carlo yield predictions with 1,000 simulations</p>
            </div>
          </div>
          
          {!isElite && (
            <div className="mt-4 p-3 bg-[#ffa502]/10 border border-[#ffa502]/30 rounded-lg flex items-center gap-3">
              <Crown className="w-5 h-5 text-[#ffa502]" />
              <div className="flex-1">
                <p className="text-sm text-[#ffa502] font-medium">Elite Feature</p>
                <p className="text-xs text-gray-400">Upgrade to Elite to unlock full Forecast Dial access</p>
              </div>
            </div>
          )}
        </div>
        
        {/* Search / Manual Entry Toggle */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-4">
            <button
              onClick={() => { setManualMode(false); handleClear(); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                !manualMode 
                  ? 'bg-[#8b5cf6] text-white' 
                  : 'bg-gray-800 text-gray-400 hover:text-white'
              }`}
            >
              Search Pools
            </button>
            <button
              onClick={() => { setManualMode(true); setSelectedPool(null); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                manualMode 
                  ? 'bg-[#8b5cf6] text-white' 
                  : 'bg-gray-800 text-gray-400 hover:text-white'
              }`}
            >
              Manual Entry
            </button>
          </div>
          
          {!manualMode ? (
            /* Search Mode */
            <div className="relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    if (selectedPool) setSelectedPool(null);
                  }}
                  placeholder="Search pools by name (e.g., ETH-USDC, WBTC-ETH...)"
                  className="w-full pl-10 pr-4 py-3 bg-[#141926] border border-[#1e2a3a] rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-[#8b5cf6]"
                />
                {searching && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="w-5 h-5 border-2 border-[#8b5cf6] border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </div>
              
              {/* Search Results Dropdown */}
              {showResults && searchResults.length > 0 && !selectedPool && (
                <div className="absolute z-50 w-full mt-2 bg-[#141926] border border-[#1e2a3a] rounded-xl shadow-2xl max-h-80 overflow-y-auto">
                  {searchResults.map((pool) => (
                    <button
                      key={pool.pool_id}
                      onClick={() => handleSelectPool(pool)}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-[#1e2a3a] transition-colors border-b border-[#1e2a3a] last:border-0"
                    >
                      <div className="text-left">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-white">{pool.symbol}</span>
                          <span className="text-xs px-2 py-0.5 bg-gray-800 rounded text-gray-400">{pool.chain}</span>
                        </div>
                        <p className="text-xs text-gray-500">{pool.protocol} • TVL: {formatUSD(pool.tvl_usd)}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-[#00ff88] font-bold">{pool.apy_total?.toFixed(2)}%</div>
                        <div className="text-xs text-gray-500">APY</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              
              {/* Selected Pool Card */}
              {selectedPool && (
                <div className="mt-4 p-4 bg-[#141926] border border-[#8b5cf6]/30 rounded-xl">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-[#00ff88]/10 rounded-lg">
                        <TrendingUp className="w-5 h-5 text-[#00ff88]" />
                      </div>
                      <div>
                        <h3 className="font-bold text-white">{selectedPool.symbol}</h3>
                        <p className="text-xs text-gray-400">{selectedPool.protocol} • {selectedPool.chain}</p>
                      </div>
                    </div>
                    <button
                      onClick={handleClear}
                      className="text-xs text-gray-500 hover:text-white px-3 py-1 rounded-lg hover:bg-gray-800"
                    >
                      Change
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-lg font-bold text-[#00ff88]">{selectedPool.apy_total?.toFixed(2)}%</div>
                      <div className="text-xs text-gray-500">APY</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold text-white">{formatUSD(selectedPool.tvl_usd)}</div>
                      <div className="text-xs text-gray-500">TVL</div>
                    </div>
                    <div>
                      <div className={`text-lg font-bold ${
                        selectedPool.il_risk === 'low' ? 'text-[#10b981]' :
                        selectedPool.il_risk === 'high' ? 'text-[#ef4444]' : 'text-[#f59e0b]'
                      }`}>
                        {selectedPool.il_risk?.charAt(0).toUpperCase() + selectedPool.il_risk?.slice(1) || 'Medium'}
                      </div>
                      <div className="text-xs text-gray-500">IL Risk</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Manual Entry Mode */
            <div className="p-4 bg-[#141926] border border-[#1e2a3a] rounded-xl space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Pool Symbol *</label>
                  <input
                    type="text"
                    value={manualData.symbol}
                    onChange={(e) => setManualData(d => ({ ...d, symbol: e.target.value }))}
                    placeholder="e.g., ETH-USDC"
                    className="w-full px-3 py-2 bg-[#0a0e17] border border-[#1e2a3a] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#8b5cf6]"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Protocol</label>
                  <input
                    type="text"
                    value={manualData.protocol}
                    onChange={(e) => setManualData(d => ({ ...d, protocol: e.target.value }))}
                    placeholder="e.g., Uniswap"
                    className="w-full px-3 py-2 bg-[#0a0e17] border border-[#1e2a3a] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#8b5cf6]"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Current APY (%) *</label>
                  <input
                    type="number"
                    value={manualData.current_apy}
                    onChange={(e) => setManualData(d => ({ ...d, current_apy: e.target.value }))}
                    placeholder="e.g., 15.5"
                    min="0"
                    step="0.1"
                    className="w-full px-3 py-2 bg-[#0a0e17] border border-[#1e2a3a] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#8b5cf6]"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">TVL (USD) *</label>
                  <input
                    type="number"
                    value={manualData.tvl}
                    onChange={(e) => setManualData(d => ({ ...d, tvl: e.target.value }))}
                    placeholder="e.g., 5000000"
                    min="0"
                    className="w-full px-3 py-2 bg-[#0a0e17] border border-[#1e2a3a] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#8b5cf6]"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Chain</label>
                  <select
                    value={manualData.chain}
                    onChange={(e) => setManualData(d => ({ ...d, chain: e.target.value }))}
                    className="w-full px-3 py-2 bg-[#0a0e17] border border-[#1e2a3a] rounded-lg text-white focus:outline-none focus:border-[#8b5cf6]"
                  >
                    {CHAINS.map(chain => (
                      <option key={chain} value={chain}>{chain.charAt(0).toUpperCase() + chain.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">IL Risk</label>
                  <select
                    value={manualData.il_risk}
                    onChange={(e) => setManualData(d => ({ ...d, il_risk: e.target.value }))}
                    className="w-full px-3 py-2 bg-[#0a0e17] border border-[#1e2a3a] rounded-lg text-white focus:outline-none focus:border-[#8b5cf6]"
                  >
                    {IL_RISKS.map(risk => (
                      <option key={risk.value} value={risk.value}>{risk.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              {!manualSubmitted && (
                <div className="mt-4 space-y-3">
                  {manualFieldsReady ? (
                    <button
                      onClick={() => setManualSubmitted(true)}
                      className="w-full py-3.5 bg-gradient-to-r from-[#8b5cf6] to-[#6d28d9] text-white font-bold rounded-xl hover:from-[#7c3aed] hover:to-[#5b21b6] transition-all flex items-center justify-center gap-2 text-base shadow-lg shadow-[#8b5cf6]/25 hover:shadow-[#8b5cf6]/40 hover:scale-[1.01]"
                      data-testid="run-forecast-btn"
                    >
                      <Gauge className="w-5 h-5" />
                      Run Forecast
                    </button>
                  ) : (
                    <div className="w-full py-3.5 bg-gray-800 text-gray-500 font-medium rounded-xl flex items-center justify-center gap-2 text-sm border border-gray-700 cursor-not-allowed">
                      <Gauge className="w-5 h-5" />
                      Fill in Symbol, APY & TVL to run forecast
                    </div>
                  )}
                </div>
              )}
              {manualSubmitted && (
                <div className="mt-4 flex gap-2">
                  <button
                    onClick={() => { setManualSubmitted(false); }}
                    className="flex-1 py-2.5 bg-gray-800 text-gray-300 rounded-xl hover:bg-gray-700 hover:text-white transition-all flex items-center justify-center gap-2 text-sm font-medium border border-gray-700"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    Edit & Re-run
                  </button>
                  <button
                    onClick={handleClear}
                    className="px-4 py-2.5 bg-gray-800 text-gray-400 rounded-xl hover:bg-gray-700 hover:text-white transition-all text-sm border border-gray-700"
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Forecast Dial */}
        {poolData ? (
          <div className="mt-8">
            <ForecastDial
              poolId={poolData.poolId}
              symbol={poolData.symbol}
              currentApy={poolData.currentApy}
              tvl={poolData.tvl}
              ilRisk={poolData.ilRisk}
              chain={poolData.chain}
              protocol={poolData.protocol}
              userTier={userTier}
              isEliteOverride={isElite}
              onUpgrade={() => window.dispatchEvent(new CustomEvent('profitspot:open-compare-plans'))}
              apiUrl={API_URL}
              token={token}
            />
          </div>
        ) : (
          <div className="mt-8 p-12 bg-[#141926] border border-[#1e2a3a] rounded-xl text-center">
            <Gauge className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-400 mb-2">Select a Pool to Forecast</h3>
            <p className="text-sm text-gray-500 max-w-md mx-auto">
              Search for a pool from our database of 7,000+ DeFi pools, or enter pool details manually to run a Monte Carlo simulation.
            </p>
          </div>
        )}
        
        {/* Info Section */}
        <div className="mt-8 p-4 bg-[#141926]/50 border border-[#1e2a3a] rounded-xl">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-[#8b5cf6] flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-white mb-1">How Forecast Dial Works</h4>
              <p className="text-sm text-gray-400 leading-relaxed">
                The Forecast Dial runs 1,000 Monte Carlo simulations using the Ornstein-Uhlenbeck process to model APY mean-reversion. 
                It factors in IL risk, TVL stability, and historical volatility to give you a <strong className="text-[#8b5cf6]">ProfitSpot Score (0-100)</strong> and 
                probability of profit for your chosen time horizon.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="text-xs px-2 py-1 bg-[#ef4444]/20 text-[#ef4444] rounded">0-30: Danger</span>
                <span className="text-xs px-2 py-1 bg-[#f59e0b]/20 text-[#f59e0b] rounded">31-55: Caution</span>
                <span className="text-xs px-2 py-1 bg-[#10b981]/20 text-[#10b981] rounded">56-80: Favorable</span>
                <span className="text-xs px-2 py-1 bg-[#06b6d4]/20 text-[#06b6d4] rounded">81-100: Optimal</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
